// src/screens/TeamStats.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { AnimatePresence, motion } from "framer-motion";
import { BarChart2, Flame, Info, Lock, Dumbbell, TrendingUp, UserCheck, ShieldAlert } from "lucide-react";
import { LogoLoader } from "../components/LogoLoader";
import { Tooltip } from "../components/Tooltip";
import { ExportButtons } from "../components/ExportButtons";
import { ShotHeatmapCanvas } from "../components/ShotHeatmapCanvas";
import { CourtZoneSVGOverlay } from "../components/CourtZoneSVGOverlay";

type Shot = { x: number; y: number; made: boolean; player_number: number };

type Training = { id: string; date: string; notes: string | null };

type AttendanceRecord = { training_id: string; player_json_id: string | null; player_id: string | null; was_present: boolean };

type PlayerAttendance = {
  player_json_id: string;
  display_name: string;
  present: number;
  absent: number;
  total: number;
  rate: number;
};

type PlayerStat = {
  player_number: number;
  player_name: string;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
  games: number;
};

function safePct(n: number, d: number) {
  if (!d) return "–";
  return ((n / d) * 100).toFixed(1) + "%";
}

export default function ClubTeamStats() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();

  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  // ── Team settings / permissions ──
  const [permLoading, setPermLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  const [statsCoachesOnly, setStatsCoachesOnly] = useState(false);
  const [playersOwnStatsOnly, setPlayersOwnStatsOnly] = useState(false);
  const [ownPlayerNumber, setOwnPlayerNumber] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<"stats" | "heatmap" | "trainings">("stats");

  // ── Training attendance state ──
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [playerAttendance, setPlayerAttendance] = useState<PlayerAttendance[]>([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState("");

  const [allStats, setAllStats] = useState<PlayerStat[]>([]);
  const [filteredStats, setFilteredStats] = useState<PlayerStat[]>([]);
  const [search, setSearch] = useState("");

  const [selectedPlayer, setSelectedPlayer] = useState<PlayerStat | null>(null);

  const [allShots, setAllShots] = useState<Shot[]>([]);
  const [selectedPlayerNumber, setSelectedPlayerNumber] = useState<string>("");
  const filteredShots =
    selectedPlayerNumber === ""
      ? allShots
      : allShots.filter((s) => s.player_number.toString() === selectedPlayerNumber);

  // ── Derived visibility from team_settings ──
  const statsRestricted = statsCoachesOnly && !isStaff;
  const ownStatsOnly =
    !statsRestricted && playersOwnStatsOnly && !isStaff && ownPlayerNumber != null;

  const visibleStats = useMemo(
    () => (ownStatsOnly ? filteredStats.filter((s) => s.player_number === ownPlayerNumber) : filteredStats),
    [filteredStats, ownStatsOnly, ownPlayerNumber]
  );

  const visibleShots = ownStatsOnly
    ? allShots.filter((s) => s.player_number === ownPlayerNumber)
    : filteredShots;

  const [heatmapSubTab, setHeatmapSubTab] = useState<"tirs" | "zones">("tirs");
  const MIN_SHOTS = 20;

  // premium user (même logique que ton app coach)
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) return;

      const { data } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();

      if (data?.is_premium) setIsPremium(true);
    })();
  }, []);

  // Role + team_settings (gates stats visibility)
  useEffect(() => {
    (async () => {
      if (!teamId || !clubId) { setPermLoading(false); return; }

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) { setPermLoading(false); return; }

      const [teamRoleRes, clubRoleRes, settingsRes] = await Promise.all([
        supabase
          .from("team_members")
          .select("role, player_number")
          .eq("team_id", teamId)
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("club_members")
          .select("role")
          .eq("club_id", clubId)
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("team_settings")
          .select("stats_visible_to_coaches_only, players_see_own_stats_only")
          .eq("team_id", teamId)
          .maybeSingle(),
      ]);

      const teamRole = teamRoleRes.data?.role ?? null;
      const clubRole = clubRoleRes.data?.role ?? null;
      const staff =
        ["owner", "coach", "assistant"].includes(teamRole ?? "") ||
        ["dirigeant", "cadre_technique"].includes(clubRole ?? "");

      setIsStaff(staff);
      setOwnPlayerNumber(teamRoleRes.data?.player_number ?? null);
      setStatsCoachesOnly(settingsRes.data?.stats_visible_to_coaches_only ?? false);
      setPlayersOwnStatsOnly(settingsRes.data?.players_see_own_stats_only ?? false);
      setPermLoading(false);
    })();
  }, [teamId, clubId]);

  // Load shots for this team
  useEffect(() => {
    (async () => {
      if (!teamId) return;

      const { data: matchList, error: matchError } = await supabase
        .from("matches")
        .select("id")
        .eq("team_id", teamId);

      if (matchError || !matchList) {
        console.error("[teamstats] matches error:", matchError);
        setAllShots([]);
        return;
      }

      const matchIds = matchList.map((m) => m.id);
      if (matchIds.length === 0) {
        setAllShots([]);
        return;
      }

      const { data: shotsData, error: shotsError } = await supabase
        .from("shot_locations")
        .select("*")
        .in("match_id", matchIds);

      if (shotsError) {
        console.error("[teamstats] shot_locations error:", shotsError);
        setAllShots([]);
        return;
      }

      setAllShots(
        (shotsData || []).map((s: any) => ({
          x: s.x,
          y: s.y,
          made: s.made,
          player_number: s.player_number,
        }))
      );
    })();
  }, [teamId]);

  // Load stats for this team
  useEffect(() => {
    (async () => {
      if (!teamId) return;
      setLoading(true);

      const { data: matchList, error: mErr } = await supabase
        .from("matches")
        .select("id")
        .eq("team_id", teamId);

      if (mErr) {
        console.error("[teamstats] matches error:", mErr);
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }

      const matchIds = (matchList || []).map((m) => m.id);
      if (matchIds.length === 0) {
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("match_stats")
        .select("*")
        .in("match_id", matchIds);

      if (error) {
        console.error("[teamstats] match_stats error:", error);
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }

      const statsByPlayer: Record<string, PlayerStat> = {};
      (data || []).forEach((stat: any) => {
        const key = `${stat.player_number}-${stat.player_name}`;
        if (!statsByPlayer[key]) {
          statsByPlayer[key] = { ...stat, games: 1 };
        } else {
          statsByPlayer[key].points += stat.points;
          statsByPlayer[key].assists += stat.assists;
          statsByPlayer[key].rebounds += stat.rebounds;
          statsByPlayer[key].steals += stat.steals;
          statsByPlayer[key].blocks += stat.blocks;
          statsByPlayer[key].turnovers += stat.turnovers;
          statsByPlayer[key].shots_attempted += stat.shots_attempted;
          statsByPlayer[key].shots_made += stat.shots_made;
          statsByPlayer[key].ft_attempted += stat.ft_attempted;
          statsByPlayer[key].ft_made += stat.ft_made;
          statsByPlayer[key].three_attempted += stat.three_attempted;
          statsByPlayer[key].three_made += stat.three_made;
          statsByPlayer[key].games += 1;
        }
      });

      const finalStats = Object.values(statsByPlayer);
      setAllStats(finalStats);
      setFilteredStats(finalStats);
      setLoading(false);
    })();
  }, [teamId]);

  // Load training attendance — triggered when user opens the tab
  useEffect(() => {
    if (activeTab !== "trainings" || !teamId) return;
    let cancelled = false;

    async function loadTrainings() {
      setTrainingLoading(true);
      setTrainingError("");

      try {
        // 1. Get all trainings for this team
        const { data: trainingRows, error: tErr } = await supabase
          .from("trainings")
          .select("id, date, notes")
          .eq("team_id", teamId)
          .order("date", { ascending: false });

        if (tErr) throw tErr;
        const trainingList = (trainingRows ?? []) as Training[];
        if (cancelled) return;
        setTrainings(trainingList);

        if (trainingList.length === 0) { setTrainingLoading(false); return; }

        const trainingIds = trainingList.map((t) => t.id);

        // 2. Get attendance records
        const { data: attendanceRows, error: aErr } = await supabase
          .from("training_attendance")
          .select("training_id, player_json_id, player_id, was_present")
          .in("training_id", trainingIds);

        if (aErr) throw aErr;
        const records = (attendanceRows ?? []) as AttendanceRecord[];

        // 3. Map player_json_id → display name
        // Primary source: teams.players JSON array (covers players without user accounts)
        // Fallback: team_members → user_profiles
        const nameMap = new Map<string, string>(); // player_json_id → display name

        const { data: teamRow } = await supabase
          .from("teams")
          .select("players")
          .eq("id", teamId)
          .maybeSingle();

        const rawPlayers = teamRow?.players;
        const playersArray: any[] = Array.isArray(rawPlayers)
          ? rawPlayers
          : typeof rawPlayers === "string"
          ? (() => { try { return JSON.parse(rawPlayers); } catch { return []; } })()
          : [];

        for (const p of playersArray) {
          if (p.id) {
            const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim()
              || (p.number != null ? `#${p.number}` : null)
              || "Joueur";
            nameMap.set(String(p.id), name);
          }
        }

        // Enrich with profile names for players who have linked accounts
        const playerJsonIds = [...new Set(records.map((r) => r.player_json_id).filter(Boolean))] as string[];
        if (playerJsonIds.length > 0) {
          const { data: members } = await supabase
            .from("team_members")
            .select("player_json_id, player_number, user_id")
            .eq("team_id", teamId)
            .in("player_json_id", playerJsonIds);

          const memberUserIds = (members ?? []).map((m: any) => m.user_id).filter(Boolean) as string[];
          if (memberUserIds.length > 0) {
            const { data: profiles } = await supabase
              .from("user_profiles")
              .select("id, full_name, pseudo, public_use_pseudo")
              .in("id", memberUserIds);
            const profileMap = new Map<string, string>();
            for (const p of profiles ?? []) {
              profileMap.set(p.id, p.public_use_pseudo && p.pseudo ? p.pseudo : p.full_name?.trim() || "");
            }
            for (const m of members ?? []) {
              const profileName = m.user_id ? profileMap.get(m.user_id) : "";
              if (profileName && m.player_json_id) nameMap.set(m.player_json_id, profileName);
            }
          }
        }

        // 4. Aggregate per player_json_id
        const totalPast = trainingList.filter((t) => new Date(t.date) <= new Date()).length;
        const byPlayer = new Map<string, { present: number; absent: number }>();
        for (const r of records) {
          const key = r.player_json_id ?? r.player_id ?? "unknown";
          if (!byPlayer.has(key)) byPlayer.set(key, { present: 0, absent: 0 });
          const entry = byPlayer.get(key)!;
          if (r.was_present) entry.present++;
          else entry.absent++;
        }

        const aggregated: PlayerAttendance[] = Array.from(byPlayer.entries()).map(([key, counts]) => {
          const total = counts.present + counts.absent;
          return {
            player_json_id: key,
            display_name: nameMap.get(key) ?? "Joueur inconnu",
            present: counts.present,
            absent: counts.absent,
            total,
            rate: totalPast > 0 ? (counts.present / totalPast) * 100 : 0,
          };
        }).sort((a, b) => b.rate - a.rate);

        if (!cancelled) { setPlayerAttendance(aggregated); }
      } catch (err: any) {
        if (!cancelled) setTrainingError(err?.message ?? "Impossible de charger les données.");
      } finally {
        if (!cancelled) setTrainingLoading(false);
      }
    }

    loadTrainings();
    return () => { cancelled = true; };
  }, [activeTab, teamId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    setFilteredStats(
      allStats.filter(
        (s) =>
          s.player_name.toLowerCase().includes(value) ||
          s.player_number.toString().includes(value)
      )
    );
  };

  const calculateTS = (points: number, fga: number, fta: number): string => {
    const denominator = 2 * (fga + 0.44 * fta);
    if (denominator === 0) return "–";
    return ((points / denominator) * 100).toFixed(1) + " %";
  };

  const calculateEFG = (fgm: number, fga: number, threeMade: number): string => {
    if (fga === 0) return "–";
    return (((fgm + 0.5 * threeMade) / fga) * 100).toFixed(1) + " %";
  };

  const calculateEFF = (s: PlayerStat): string => {
    const missedFG = s.shots_attempted - s.shots_made;
    const missedFT = s.ft_attempted - s.ft_made;
    const eff =
      (s.points +
        s.rebounds +
        s.assists +
        s.steals +
        s.blocks -
        s.turnovers -
        missedFG -
        missedFT) /
      s.games;
    return eff.toFixed(1);
  };

  const totals = useMemo(() => {
    const totalShotsMade = visibleStats.reduce((sum, p) => sum + p.shots_made, 0);
    const totalShotsAttempted = visibleStats.reduce((sum, p) => sum + p.shots_attempted, 0);
    const totalFTMade = visibleStats.reduce((sum, p) => sum + p.ft_made, 0);
    const totalFTAttempted = visibleStats.reduce((sum, p) => sum + p.ft_attempted, 0);
    const totalThreeMade = visibleStats.reduce((sum, p) => sum + p.three_made, 0);
    const totalThreeAttempted = visibleStats.reduce((sum, p) => sum + p.three_attempted, 0);

    const teamTotals = {
      points: visibleStats.reduce((sum, p) => sum + p.points, 0),
      shots_made: totalShotsMade,
      shots_attempted: totalShotsAttempted,
      ft_made: totalFTMade,
      ft_attempted: totalFTAttempted,
      three_made: totalThreeMade,
      three_attempted: totalThreeAttempted,
      rebounds: visibleStats.reduce((sum, p) => sum + p.rebounds, 0),
      assists: visibleStats.reduce((sum, p) => sum + p.assists, 0),
      steals: visibleStats.reduce((sum, p) => sum + p.steals, 0),
      blocks: visibleStats.reduce((sum, p) => sum + p.blocks, 0),
      turnovers: visibleStats.reduce((sum, p) => sum + p.turnovers, 0),
    };

    return { teamTotals, totalShotsMade, totalShotsAttempted, totalFTMade, totalFTAttempted, totalThreeMade, totalThreeAttempted };
  }, [visibleStats]);

  if (loading || permLoading) return <LogoLoader />;

  if (statsRestricted) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
        <ShieldAlert className="h-8 w-8 text-gray-400" />
        <div className="text-sm text-center">
          Les statistiques de cette équipe sont réservées aux coachs et dirigeants.
        </div>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      {/* Top bar (style layout club) */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
          <div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Statistiques équipe</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Stats saison • Joueurs
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Tableau, radar individuel, et (premium) heatmap.
            </div>
          </div>

          <Link
            to="/glossaire-stats"
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
          >
            <Info className="h-4 w-4" />
            Glossaire
          </Link>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab("stats")}
            className={activeTab === "stats"
              ? "px-4 py-2 rounded-xl font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 inline-flex items-center gap-2"
              : "px-4 py-2 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 inline-flex items-center gap-2"}
          >
            <BarChart2 className="h-4 w-4" />
            Stats
          </button>

          {isPremium ? (
            <button
              onClick={() => setActiveTab("heatmap")}
              className={activeTab === "heatmap"
                ? "px-4 py-2 rounded-xl font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 inline-flex items-center gap-2"
                : "px-4 py-2 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 inline-flex items-center gap-2"}
            >
              <Flame className="h-4 w-4" />
              Heatmap
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 rounded-xl font-semibold bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400 inline-flex items-center gap-2 cursor-not-allowed"
              title="Fonction Premium"
            >
              <Flame className="h-4 w-4" />
              Heatmap
              <Lock className="h-4 w-4" />
            </button>
          )}

          <button
            onClick={() => setActiveTab("trainings")}
            className={activeTab === "trainings"
              ? "px-4 py-2 rounded-xl font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 inline-flex items-center gap-2"
              : "px-4 py-2 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 inline-flex items-center gap-2"}
          >
            <Dumbbell className="h-4 w-4" />
            Entraînements
          </button>
        </div>
      </div>

      {/* STATS */}
      {activeTab === "stats" && (
        <>
          {ownStatsOnly ? (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
              Le staff a limité l'affichage des statistiques : tu ne vois que tes propres stats.
            </div>
          ) : (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <input
                type="text"
                value={search}
                onChange={handleSearch}
                placeholder="Rechercher un joueur (nom ou numéro)"
                className="w-full md:w-1/2 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm outline-none"
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800/80">
                <tr className="text-center">
                  <th className="p-2">#</th>
                  <th className="p-2 text-left">Joueur</th>
                  <th className="p-2">Poste</th>
                  <th className="p-2">MJ</th>
                  <th className="p-2">PTS</th>
                  <th className="p-2">PTS/M</th>
                  <th className="p-2">AST</th>
                  <th className="p-2">REB</th>
                  <th className="p-2">STL</th>
                  <th className="p-2">BLK</th>
                  <th className="p-2">TO</th>
                  <th className="p-2">FG</th>
                  <th className="p-2">FG%</th>
                  <th className="p-2">FT</th>
                  <th className="p-2">3PT</th>
                  <th className="p-2">Éval</th>

                  {isPremium && (
                    <th className="p-2">
                      <div className="flex items-center justify-center gap-1">
                        eFG% <Tooltip text="(FGM + 0.5×3PM) / FGA" />
                      </div>
                    </th>
                  )}
                  {isPremium && (
                    <th className="p-2">
                      <div className="flex items-center justify-center gap-1">
                        TS% <Tooltip text="PTS / (2×(FGA + 0.44×FTA))" />
                      </div>
                    </th>
                  )}
                  {isPremium && (
                    <th className="p-2">
                      <div className="flex items-center justify-center gap-1">
                        EFF <Tooltip text="(PTS + REB + AST + STL + BLK − TO − missFG − missFT) / MJ" />
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="text-center">
                {visibleStats.map((s, idx) => {
                  const evalAvg =
                    (s.points +
                      s.rebounds +
                      s.assists +
                      s.steals +
                      s.blocks -
                      s.turnovers +
                      s.shots_made -
                      s.shots_attempted) /
                    s.games;

                  return (
                    <tr
                      key={`${s.player_number}-${idx}`}
                      className="border-t border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => setSelectedPlayer(s)}
                    >
                      <td className="p-2">{s.player_number}</td>
                      <td className="p-2 text-left font-medium text-gray-900 dark:text-gray-100">
                        {s.player_name}
                      </td>
                      <td className="p-2">{s.position}</td>
                      <td className="p-2">{s.games}</td>
                      <td className="p-2">{s.points}</td>
                      <td className="p-2">{(s.points / s.games).toFixed(1)}</td>
                      <td className="p-2">{s.assists}</td>
                      <td className="p-2">{s.rebounds}</td>
                      <td className="p-2">{s.steals}</td>
                      <td className="p-2">{s.blocks}</td>
                      <td className="p-2">{s.turnovers}</td>
                      <td className="p-2">
                        {s.shots_made}/{s.shots_attempted}
                      </td>
                      <td className="p-2">{safePct(s.shots_made, s.shots_attempted)}</td>
                      <td className="p-2">
                        {s.ft_made}/{s.ft_attempted}
                      </td>
                      <td className="p-2">
                        {s.three_made}/{s.three_attempted}
                      </td>
                      <td className="p-2">{evalAvg.toFixed(1)}</td>

                      {isPremium && (
                        <td className="p-2">
                          {calculateEFG(s.shots_made, s.shots_attempted, s.three_made)}
                        </td>
                      )}
                      {isPremium && (
                        <td className="p-2">
                          {calculateTS(s.points, s.shots_attempted, s.ft_attempted)}
                        </td>
                      )}
                      {isPremium && <td className="p-2">{calculateEFF(s)}</td>}
                    </tr>
                  );
                })}

                {/* Totaux équipe */}
                <tr className="border-t border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800/80 font-semibold">
                  <td className="p-2" colSpan={11}>
                    Totaux équipe
                  </td>
                  <td className="p-2">
                    {totals.totalShotsMade}/{totals.totalShotsAttempted}
                  </td>
                  <td className="p-2">{safePct(totals.totalShotsMade, totals.totalShotsAttempted)}</td>
                  <td className="p-2">
                    {totals.totalFTMade}/{totals.totalFTAttempted}
                  </td>
                  <td className="p-2">
                    {totals.totalThreeMade}/{totals.totalThreeAttempted}
                  </td>
                  <td className="p-2">–</td>
                  {isPremium && (
                    <td className="p-2">
                      {totals.totalShotsAttempted > 0
                        ? (((totals.totalShotsMade + 0.5 * totals.totalThreeMade) / totals.totalShotsAttempted) * 100).toFixed(1) + " %"
                        : "–"}
                    </td>
                  )}
                  {isPremium && (
                    <td className="p-2">
                      {calculateTS(
                        totals.teamTotals.points,
                        totals.teamTotals.shots_attempted,
                        totals.teamTotals.ft_attempted
                      )}
                    </td>
                  )}
                  {isPremium && <td className="p-2">–</td>}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Exports (réutilise ton composant) */}
          <ExportButtons
            data={visibleStats}
            fileName={`team-stats-${teamId}`}
            excelColumns={{
              Num: "player_number",
              Nom: "player_name",
              Poste: "position",
              Matchs: "games",
              Points: "points",
              Passes: "assists",
              Rebonds: "rebounds",
              Interceptions: "steals",
              Contres: "blocks",
              Pertes: "turnovers",
              FGM: "shots_made",
              FGA: "shots_attempted",
              FTM: "ft_made",
              FTA: "ft_attempted",
              "3PM": "three_made",
              "3PA": "three_attempted",
            }}
            pdfColumns={[
              { header: "#", key: "player_number" },
              { header: "Nom", key: "player_name" },
              { header: "Poste", key: "position" },
              { header: "MJ", key: "games" },
              { header: "PTS", key: "points" },
              { header: "AST", key: "assists" },
              { header: "REB", key: "rebounds" },
              { header: "STL", key: "steals" },
              { header: "BLK", key: "blocks" },
              { header: "TO", key: "turnovers" },
              { header: "FGM", key: "shots_made" },
              { header: "FGA", key: "shots_attempted" },
            ]}
          />
        </>
      )}

      {/* HEATMAP */}
      {activeTab === "heatmap" && (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          {ownStatsOnly ? (
            <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Le staff a limité l'affichage des statistiques : tu ne vois que tes propres tirs.
            </div>
          ) : (
            <select
              value={selectedPlayerNumber}
              onChange={(e) => setSelectedPlayerNumber(e.target.value)}
              className="mb-4 w-full md:w-1/2 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm"
            >
              <option value="">Toute l’équipe</option>
              {allStats.map((player) => (
                <option key={player.player_number} value={player.player_number.toString()}>
                  {player.player_number} – {player.player_name}
                </option>
              ))}
            </select>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setHeatmapSubTab("tirs")}
              className={heatmapSubTab === "tirs"
                ? "px-4 py-2 rounded-xl font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 inline-flex items-center gap-2"
                : "px-4 py-2 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 inline-flex items-center gap-2"}
            >
              <Flame className="h-4 w-4" /> Tirs
            </button>

            <button
              onClick={() => setHeatmapSubTab("zones")}
              className={heatmapSubTab === "zones"
                ? "px-4 py-2 rounded-xl font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 inline-flex items-center gap-2"
                : "px-4 py-2 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 inline-flex items-center gap-2"}
            >
              <BarChart2 className="h-4 w-4" /> Zones
            </button>
          </div>

          {visibleShots.length < MIN_SHOTS && (
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
              Minimum {MIN_SHOTS} tirs requis pour une heatmap fiable.
            </p>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-3">
            {heatmapSubTab === "tirs" ? (
              <ShotHeatmapCanvas shots={visibleShots} />
            ) : (
              <CourtZoneSVGOverlay shots={visibleShots} />
            )}
          </div>
        </div>
      )}

      {/* ENTRAÎNEMENTS */}
      {activeTab === "trainings" && (
        <div className="space-y-4">
          {trainingLoading ? (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
              Chargement des entraînements…
            </div>
          ) : trainingError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
              {trainingError.includes("does not exist") || trainingError.includes("relation")
                ? "La table de présences n'existe pas encore. Exécutez le script SQL fourni pour l'activer."
                : trainingError}
            </div>
          ) : (
            <>
              {/* Summary cards */}
              {(() => {
                const past = trainings.filter((t) => new Date(t.date) <= new Date());
                const upcoming = trainings.filter((t) => new Date(t.date) > new Date());
                const totalResponses = playerAttendance.reduce((s, p) => s + p.total, 0);
                const totalPresent = playerAttendance.reduce((s, p) => s + p.present, 0);
                const avgRate = totalResponses > 0 ? (totalPresent / totalResponses) * 100 : null;
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "Total", value: trainings.length, color: "text-gray-900 dark:text-gray-100" },
                      { label: "Passés", value: past.length, color: "text-gray-600 dark:text-gray-300" },
                      { label: "À venir", value: upcoming.length, color: "text-indigo-700 dark:text-indigo-300" },
                      { label: "Taux moyen", value: avgRate !== null ? avgRate.toFixed(0) + "%" : "–", color: avgRate !== null && avgRate >= 70 ? "text-green-700 dark:text-green-400" : "text-amber-700 dark:text-amber-400" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                        <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Per-player attendance table */}
              {playerAttendance.length > 0 ? (
                <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Présences par joueur</span>
                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{playerAttendance.length} joueur{playerAttendance.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr className="text-center">
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Joueur</th>
                          <th className="px-3 py-2 text-xs font-semibold text-green-700 dark:text-green-400">Présent</th>
                          <th className="px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-400">Absent</th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Total</th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-900 dark:text-gray-100">Taux</th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 w-32">Présence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerAttendance.map((p) => (
                            <tr key={p.player_json_id} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                              <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{p.display_name}</td>
                              <td className="px-3 py-2.5 text-center text-green-700 dark:text-green-400 font-semibold">{p.present}</td>
                              <td className="px-3 py-2.5 text-center text-red-600 dark:text-red-400">{p.absent}</td>
                              <td className="px-3 py-2.5 text-center text-gray-500 dark:text-gray-400">{p.total}</td>
                              <td className="px-3 py-2.5 text-center font-bold text-gray-900 dark:text-gray-100">{p.rate.toFixed(0)}%</td>
                              <td className="px-3 py-2.5">
                                <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all ${p.rate >= 75 ? "bg-green-500" : p.rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                                    style={{ width: `${Math.min(p.rate, 100)}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
                  <TrendingUp className="h-8 w-8" />
                  <div className="text-sm text-center">
                    {trainings.length === 0
                      ? "Aucun entraînement enregistré pour cette équipe."
                      : "Aucune présence enregistrée. Marquez les présences depuis le détail d'un entraînement."}
                  </div>
                </div>
              )}

              {/* Training list */}
              {trainings.length > 0 && (
                <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                    <Dumbbell className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Liste des entraînements</span>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {trainings.map((t) => {
                      const d = new Date(t.date);
                      const past = d <= new Date();
                      const attendees = playerAttendance.filter((p) => p.present > 0).length;
                      return (
                        <div key={t.id} className="flex items-center gap-4 px-5 py-3">
                          <div className="shrink-0 w-12 text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-none">{d.getDate().toString().padStart(2, "0")}</div>
                            <div className="text-xs text-gray-500 uppercase">{d.toLocaleDateString("fr-FR", { month: "short" })}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {d.toLocaleDateString("fr-FR", { weekday: "long" }).replace(/^\w/, (c) => c.toUpperCase())}
                              {" · "}
                              {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                            {t.notes && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.notes}</div>}
                          </div>
                          <div className="shrink-0 text-right">
                            {past ? (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {playerAttendance.length > 0 ? `${attendees} présent${attendees !== 1 ? "s" : ""}` : "—"}
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">À venir</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Radar modal */}
      <AnimatePresence>
        {selectedPlayer && (
          <motion.div
            className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPlayer(null)}
          >
            <motion.div
              className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-2xl shadow-lg p-6 w-full max-w-md relative border border-gray-200 dark:border-gray-800"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold text-center mb-4">
                Radar • {selectedPlayer.player_name}
              </h2>

              <div className="text-gray-900 dark:text-gray-100">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart
                    data={[
                      { stat: "PTS", value: selectedPlayer.points / selectedPlayer.games },
                      { stat: "AST", value: selectedPlayer.assists / selectedPlayer.games },
                      { stat: "REB", value: selectedPlayer.rebounds / selectedPlayer.games },
                      { stat: "STL", value: selectedPlayer.steals / selectedPlayer.games },
                      { stat: "BLK", value: selectedPlayer.blocks / selectedPlayer.games },
                      { stat: "TO", value: selectedPlayer.turnovers / selectedPlayer.games },
                    ]}
                  >
                    <PolarGrid stroke="currentColor" strokeOpacity={0.2} />
                    <PolarAngleAxis dataKey="stat" tick={{ fill: "currentColor" }} />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: "currentColor" }} />
                    <Radar dataKey="value" stroke="#fc7825" fill="#fc7825" fillOpacity={0.6} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <button
                onClick={() => setSelectedPlayer(null)}
                className="mt-4 w-full bg-gray-900 text-white py-2 rounded-xl hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              >
                Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
